import { supabase } from '../lib/supabase'
import type { Invoice } from '../types/index'

function rowToInvoice(r: Record<string, unknown>): Invoice {
  return {
    id:            r.id as string,
    invoiceNumber: r.invoice_number as string,
    clientId:      (r.client_id as string) || '',
    clientName:    (r.client_name as string) || '',
    amount:        (r.amount as number) || 0,
    tax:           (r.tax as number) || 0,
    total:         (r.total as number) || 0,
    status:        (r.status as Invoice['status']) || 'draft',
    issuedAt:      r.issued_at as string,
    dueDate:       (r.due_date as string) || '',
    paidAt:        r.paid_at as string | undefined,
    items:         (r.items as Invoice['items']) || [],
  }
}

export async function getInvoices(): Promise<Invoice[]> {
  const { data, error } = await supabase.from('invoices').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map(rowToInvoice)
}

export async function getInvoicesByClient(clientId: string): Promise<Invoice[]> {
  const { data, error } = await supabase.from('invoices').select('*')
    .eq('client_id', clientId).order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map(rowToInvoice)
}

export async function createInvoice(inv: Omit<Invoice, 'id'>): Promise<Invoice> {
  const { data, error } = await supabase.from('invoices').insert({
    invoice_number: inv.invoiceNumber, client_id: inv.clientId, client_name: inv.clientName,
    amount: inv.amount, tax: inv.tax, total: inv.total, status: inv.status,
    issued_at: inv.issuedAt, due_date: inv.dueDate, paid_at: inv.paidAt, items: inv.items,
  }).select().single()
  if (error) throw error
  return rowToInvoice(data)
}

export async function updateInvoice(id: string, inv: Partial<Invoice>): Promise<void> {
  const updates: Record<string, unknown> = {}
  if (inv.invoiceNumber !== undefined) updates.invoice_number = inv.invoiceNumber
  if (inv.clientId      !== undefined) updates.client_id      = inv.clientId
  if (inv.clientName    !== undefined) updates.client_name    = inv.clientName
  if (inv.amount        !== undefined) updates.amount         = inv.amount
  if (inv.tax           !== undefined) updates.tax            = inv.tax
  if (inv.total         !== undefined) updates.total          = inv.total
  if (inv.status        !== undefined) updates.status         = inv.status
  if (inv.issuedAt      !== undefined) updates.issued_at      = inv.issuedAt
  if (inv.dueDate       !== undefined) updates.due_date       = inv.dueDate
  if (inv.paidAt        !== undefined) updates.paid_at        = inv.paidAt
  if (inv.items         !== undefined) updates.items          = inv.items
  const { error } = await supabase.from('invoices').update(updates).eq('id', id)
  if (error) throw error
}

export async function deleteInvoice(id: string): Promise<void> {
  const { error } = await supabase.from('invoices').delete().eq('id', id)
  if (error) throw error
}
